import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildTourStorageKey, createLocalTourPersistence } from './persistence';
import { PRODUCT_TOUR_RESTART_EVENT } from './restart';
import type {
  ProductTourDefinition,
  ProductTourHost,
  ProductTourStep,
} from './types';

type NavigationDirection = 'forward' | 'back';

export function useProductTour({
  definition,
  enabled,
  host,
  onComplete,
  conditionSnapshot = {},
}: {
  definition: ProductTourDefinition;
  enabled: boolean;
  host: ProductTourHost;
  onComplete?: () => void;
  conditionSnapshot?: Record<string, boolean>;
}) {
  const storageKey = useMemo(
    () => buildTourStorageKey(definition),
    [definition],
  );
  const persistence = useMemo(
    () => createLocalTourPersistence({ storageKey }),
    [storageKey],
  );

  const [mounted, setMounted] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const navigationDirectionRef = useRef<NavigationDirection>('forward');
  const previousConditionRef = useRef<Record<string, boolean>>({});

  const steps = definition.steps;
  const totalSteps = steps.length;
  const step: ProductTourStep | undefined = steps[stepIndex];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !enabled) {
      return;
    }
    if (!persistence.isCompleted()) {
      setIsOpen(true);
    }
  }, [enabled, mounted, persistence]);

  const finish = useCallback(() => {
    persistence.markCompleted();
    setIsOpen(false);
    setStepIndex(0);
    navigationDirectionRef.current = 'forward';
  }, [persistence]);

  const restart = useCallback(() => {
    persistence.clearCompleted();
    navigationDirectionRef.current = 'forward';
    setStepIndex(0);
    setIsOpen(true);
  }, [persistence]);

  useEffect(() => {
    if (!mounted || !enabled) {
      return undefined;
    }

    const onRestartRequested = (event: Event) => {
      if (!(event instanceof CustomEvent)) {
        return;
      }
      const detail = event.detail as { tourId?: string } | undefined;
      if (detail?.tourId !== definition.id) {
        return;
      }
      restart();
    };

    window.addEventListener(PRODUCT_TOUR_RESTART_EVENT, onRestartRequested);
    return () => {
      window.removeEventListener(
        PRODUCT_TOUR_RESTART_EVENT,
        onRestartRequested,
      );
    };
  }, [definition.id, enabled, mounted, restart]);

  const completeRun = useCallback(() => {
    finish();
    onComplete?.();
  }, [finish, onComplete]);

  const goNext = useCallback(() => {
    navigationDirectionRef.current = 'forward';
    setStepIndex((current) => {
      if (current >= totalSteps - 1) {
        completeRun();
        return current;
      }
      return current + 1;
    });
  }, [completeRun, totalSteps]);

  const goBack = useCallback(() => {
    navigationDirectionRef.current = 'back';
    setStepIndex((current) => Math.max(0, current - 1));
  }, []);

  useEffect(() => {
    if (!mounted || !enabled || !definition.keyboardShortcut) {
      return undefined;
    }

    const shortcut = definition.keyboardShortcut;
    const onKeyDown = (event: KeyboardEvent) => {
      const modifierPressed = navigator.platform.includes('Mac')
        ? event.metaKey
        : event.ctrlKey;
      const shiftOk = shortcut.shift ? event.shiftKey : !event.shiftKey;
      if (
        modifierPressed === shortcut.modifier &&
        shiftOk &&
        event.key.toLowerCase() === shortcut.key.toLowerCase()
      ) {
        event.preventDefault();
        restart();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [definition.keyboardShortcut, enabled, mounted, restart]);

  useEffect(() => {
    if (!isOpen || !step?.enterEffectId) {
      return;
    }
    host.runEnterEffect?.(step.enterEffectId);
  }, [host, isOpen, step?.enterEffectId, step?.id]);

  const conditionId =
    step?.advance.type === 'hostCondition' ? step.advance.conditionId : null;
  const conditionSatisfied =
    conditionId !== null
      ? (conditionSnapshot[conditionId] ?? host.checkCondition(conditionId))
      : false;

  useEffect(() => {
    if (!isOpen || step?.advance.type !== 'hostCondition') {
      if (conditionId) {
        previousConditionRef.current[conditionId] = conditionSatisfied;
      }
      return;
    }

    const id = step.advance.conditionId;
    const wasSatisfied = previousConditionRef.current[id] ?? false;
    previousConditionRef.current[id] = conditionSatisfied;

    if (navigationDirectionRef.current === 'back') {
      return;
    }

    if (conditionSatisfied && (wasSatisfied || conditionSatisfied)) {
      navigationDirectionRef.current = 'forward';
      setStepIndex((current) => {
        const currentStep = steps[current];
        if (currentStep?.advance.type !== 'hostCondition') {
          return current;
        }
        if (currentStep.advance.conditionId !== id) {
          return current;
        }
        return Math.min(current + 1, totalSteps - 1);
      });
    }
  }, [conditionId, conditionSatisfied, isOpen, step, steps, totalSteps]);

  useEffect(() => {
    if (!isOpen || step?.advance.type !== 'anchorClick') {
      return undefined;
    }
    const actionId = step.actionAnchorId ?? step.anchorId;
    if (!actionId) {
      return undefined;
    }

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }
      if (target.closest(`[data-tour-id="${actionId}"]`)) {
        navigationDirectionRef.current = 'forward';
        goNext();
      }
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [goNext, isOpen, step]);

  const actionAlreadySatisfied =
    step?.advance.type === 'hostCondition' && conditionSatisfied;

  return {
    mounted,
    isOpen,
    step,
    stepIndex,
    totalSteps,
    storageKey,
    finish,
    restart,
    goNext,
    goBack,
    actionAlreadySatisfied,
  };
}
